import type {
  AcceptedOffer,
  AgentOffer,
  OfferChoice,
  OfferKind,
  OfferPreviewRow,
  OfferState,
  OfferStatus,
  SkillDiffLine,
  SkillProposal
} from "./types";

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

const KINDS: ReadonlySet<OfferKind> = new Set([
  "retitle",
  "scheme",
  "firm_style",
  "draft_timing",
  "next_steps",
  "save_skill"
]);
const STATUSES: ReadonlySet<OfferStatus> = new Set([
  "open",
  "accepted",
  "declined",
  "superseded"
]);

const choicesOf = (v: unknown): OfferChoice[] =>
  Array.isArray(v)
    ? v.flatMap((c): OfferChoice[] => {
        if (!isRecord(c) || !str(c.id) || !str(c.label)) return [];
        return [
          {
            id: c.id as string,
            label: c.label as string,
            ...(str(c.description)
              ? { description: c.description as string }
              : {}),
            ...(c.decline === true ? { decline: true } : {})
          }
        ];
      })
    : [];

const previewOf = (v: unknown): OfferPreviewRow[] =>
  Array.isArray(v)
    ? v.flatMap((r): OfferPreviewRow[] =>
        isRecord(r) && str(r.after)
          ? [{ before: str(r.before), after: r.after as string }]
          : []
      )
    : [];

const diffOf = (v: unknown): SkillDiffLine[] =>
  Array.isArray(v)
    ? v.flatMap((r): SkillDiffLine[] =>
        isRecord(r) &&
        (r.op === "add" || r.op === "remove") &&
        typeof r.text === "string"
          ? [{ op: r.op, text: r.text }]
          : []
      )
    : [];

const skillOf = (v: unknown): SkillProposal | null => {
  if (!isRecord(v)) return null;
  const skillId = str(v.skill_id);
  const title = str(v.title);
  if (!skillId || !title) return null;
  const revises =
    isRecord(v.revises) && str(v.revises.id) && str(v.revises.title)
      ? { id: v.revises.id as string, title: v.revises.title as string }
      : null;
  const diff = diffOf(v.diff);
  return {
    mode: v.mode === "revision" ? "revision" : "new",
    skillId,
    title,
    description: str(v.description) ?? "",
    ...(str(v.extends) ? { extends: v.extends as string } : {}),
    ...(revises ? { revises } : {}),
    ...(str(v.change_note) ? { changeNote: v.change_note as string } : {}),
    ...(str(v.excerpt) ? { excerpt: v.excerpt as string } : {}),
    ...(diff.length ? { diff } : {}),
    ...(str(v.reason) ? { reason: v.reason as string } : {})
  };
};

/** An offer record (`AgentOffer.public()` on the server), or null. */
export const offerOf = (o: unknown): AgentOffer | null => {
  if (!isRecord(o)) return null;
  const id = str(o.id);
  const kind = str(o.kind) as OfferKind | null;
  const choices = choicesOf(o.choices);
  if (!id || !kind || !KINDS.has(kind) || !choices.length) return null;
  const status = str(o.status) as OfferStatus | null;
  const skill = skillOf(o.skill);
  return {
    id,
    kind,
    label: str(o.label) ?? choices[0].label,
    summary: str(o.summary) ?? "",
    choices,
    status: status && STATUSES.has(status) ? status : "open",
    choice: str(o.choice),
    ...(previewOf(o.preview).length ? { preview: previewOf(o.preview) } : {}),
    ...(skill ? { skill } : {})
  };
};

/** The offer an `offer_action` result carries, or null. */
export const offerFromToolOutput = (output: unknown): AgentOffer | null =>
  isRecord(output) ? offerOf(output.offer) : null;

/** A `data-offer` part: an offer added under a finished reply (save a skill). */
export type OfferDataPart = { type: "data-offer"; id?: string; data: unknown };

export const isOfferDataPart = <P extends { type: string }>(
  part: P
): part is P & OfferDataPart =>
  part.type === "data-offer" && isRecord((part as { data?: unknown }).data);

/** The `accepted_offer` a user message carries, or null. */
export const acceptedOfferOf = (metadata: unknown): AcceptedOffer | null => {
  if (!isRecord(metadata) || !isRecord(metadata.accepted_offer)) return null;
  const id = str(metadata.accepted_offer.id);
  const choice = str(metadata.accepted_offer.choice);
  return id && choice ? { id, choice } : null;
};

type MessageLike = {
  role: string;
  parts: readonly unknown[];
  metadata?: unknown;
};

const isOfferPart = (part: unknown): part is { output?: unknown } => {
  if (!isRecord(part) || part.state !== "output-available") return false;
  if (part.type === "tool-offer_action") return true;
  return part.type === "dynamic-tool" && part.toolName === "offer_action";
};

/**
 * Every offer in the transcript, by id: the choice a later click sent, and
 * whether a newer offer of the same kind replaced it (the server refuses a
 * click on a replaced offer, so the card stops offering it).
 */
export const offerStatesInMessages = (
  messages: readonly MessageLike[]
): Map<string, OfferState> => {
  const states = new Map<string, OfferState>();
  const latestByKind = new Map<OfferKind, string>();
  for (const m of messages) {
    if (m.role === "user") {
      const accepted = acceptedOfferOf(m.metadata);
      const state = accepted ? states.get(accepted.id) : undefined;
      if (accepted && state)
        states.set(accepted.id, { ...state, choice: accepted.choice });
    } else if (m.role === "assistant") {
      const offers = m.parts
        .map((part) =>
          isOfferPart(part)
            ? offerFromToolOutput(part.output)
            : isRecord(part) && part.type === "data-offer"
            ? offerOf(part.data)
            : null
        )
        .filter((offer): offer is AgentOffer => offer !== null);
      for (const offer of offers) {
        const previous = latestByKind.get(offer.kind);
        const prior = previous ? states.get(previous) : undefined;
        if (previous && prior && !prior.choice)
          states.set(previous, { ...prior, superseded: true });
        latestByKind.set(offer.kind, offer.id);
        states.set(offer.id, { choice: offer.choice, superseded: false });
      }
    }
  }
  return states;
};
